import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { Globe2, Search, Link2, Megaphone, Info, Sparkles } from "lucide-react";

const GROUP_COLORS = {
  direct: "#6C4DF6",
  search: "#2FA36B",
  ai: "#F5A623",
  social: "#E5484D",
  referral: "#3A4A8C",
  campaign: "#9B59D0",
};

const GROUP_HINT = {
  direct: "Typed the address, used a bookmark, or came from an app or email client",
  search: "Arrived from a search engine result",
  ai: "Arrived from an AI assistant answer that cited you",
  social: "Arrived from a social network",
  referral: "Arrived from another website linking to you",
  campaign: "Arrived through a tagged link you shared (utm_source)",
};

const TrafficSources = ({ data }) => {
  if (!data) return null;
  const groups = (data.groups || []).filter((g) => g.views > 0);
  const hasAny = groups.length > 0;

  return (
    <>
      <div className="adm-two">
        <section className="adm-card">
          <h2>
            <Globe2 size={15} aria-hidden="true" /> How visitors arrive
          </h2>
          <p className="adm-sub">
            Share of page views by source, last {data.range_days} days.
          </p>
          {hasAny ? (
            <>
              <div className="adm-chart">
                <ResponsiveContainer width="100%" height={252}>
                  <PieChart>
                    <Pie
                      data={groups}
                      dataKey="views"
                      nameKey="name"
                      innerRadius={54}
                      outerRadius={92}
                      paddingAngle={2}
                    >
                      {groups.map((g) => (
                        <Cell
                          key={g.group}
                          fill={GROUP_COLORS[g.group] || "#9A94B3"}
                          stroke="#fff"
                          strokeWidth={2}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        borderRadius: 12,
                        border: "1px solid #E2DDF5",
                        fontSize: 13,
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="adm-src-legend">
                {groups.map((g) => (
                  <li key={g.group} title={GROUP_HINT[g.group] || ""}>
                    <span
                      className="adm-dot"
                      style={{ background: GROUP_COLORS[g.group] || "#9A94B3" }}
                    />
                    <b>{g.name}</b>
                    <em>
                      {g.share}% · {g.views.toLocaleString()} views · {g.visitors} visitors
                    </em>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="adm-empty">
              No traffic recorded in this range yet. Sources appear as soon as visitors
              land on the site.
            </p>
          )}
        </section>

        <section className="adm-card">
          <h2>
            <Link2 size={15} aria-hidden="true" /> Top sources
          </h2>
          <p className="adm-sub">The exact site, engine or campaign behind each visit.</p>
          {(data.top_sources || []).length ? (
            <div className="adm-chart">
              <ResponsiveContainer width="100%" height={Math.max(220, (data.top_sources || []).length * 30)}>
                <BarChart
                  data={(data.top_sources || []).slice(0, 10)}
                  layout="vertical"
                  margin={{ left: 8 }}
                >
                  <CartesianGrid stroke="#EDE9FA" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11, fill: "#5B5570" }}
                    stroke="#D8D0F4"
                    allowDecimals={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={118}
                    tick={{ fontSize: 11, fill: "#3A3358" }}
                    stroke="#D8D0F4"
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 12,
                      border: "1px solid #E2DDF5",
                      fontSize: 13,
                    }}
                  />
                  <Bar dataKey="views" name="Views" radius={[0, 6, 6, 0]} barSize={14}>
                    {(data.top_sources || []).slice(0, 10).map((s, i) => (
                      <Cell key={i} fill={GROUP_COLORS[s.group] || "#6C4DF6"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="adm-empty">Nothing to show yet.</p>
          )}
        </section>
      </div>

      <div className="adm-two">
        <section className="adm-card">
          <h2>
            <Search size={15} aria-hidden="true" /> Search engines &amp; AI answers
          </h2>
          <table className="adm-table">
            <thead>
              <tr>
                <th scope="col">Source</th>
                <th scope="col">Views</th>
                <th scope="col">Top landing page</th>
              </tr>
            </thead>
            <tbody>
              {(data.search_engines || []).map((s) => (
                <tr key={s.name}>
                  <td>{s.name}</td>
                  <td>{s.views.toLocaleString()}</td>
                  <td className="adm-mono">{s.top_landing}</td>
                </tr>
              ))}
              {!data.search_engines?.length && (
                <tr>
                  <td colSpan="3" className="adm-empty">
                    No search or AI-assistant referrals yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        <section className="adm-card">
          <h2>
            <Sparkles size={15} aria-hidden="true" /> Referring websites
          </h2>
          <table className="adm-table">
            <thead>
              <tr>
                <th scope="col">Domain</th>
                <th scope="col">Views</th>
              </tr>
            </thead>
            <tbody>
              {(data.referring_domains || []).map((d) => (
                <tr key={d.domain}>
                  <td>{d.domain}</td>
                  <td>{d.views.toLocaleString()}</td>
                </tr>
              ))}
              {!data.referring_domains?.length && (
                <tr>
                  <td colSpan="2" className="adm-empty">
                    No external links sending traffic yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </div>

      <div className="adm-two">
        <section className="adm-card">
          <h2>
            <Search size={15} aria-hidden="true" /> Search terms passed through
          </h2>
          <table className="adm-table">
            <thead>
              <tr>
                <th scope="col">Term</th>
                <th scope="col">Times</th>
              </tr>
            </thead>
            <tbody>
              {(data.search_queries || []).map((q) => (
                <tr key={q.term}>
                  <td>{q.term}</td>
                  <td>{q.count}</td>
                </tr>
              ))}
              {!data.search_queries?.length && (
                <tr>
                  <td colSpan="2" className="adm-empty">
                    None yet — most engines hide the term.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <p className="adm-note">
            <Info size={12} aria-hidden="true" /> {data.note}
          </p>
        </section>

        <section className="adm-card">
          <h2>
            <Megaphone size={15} aria-hidden="true" /> Campaign links
          </h2>
          <p className="adm-sub">
            Add <code>?utm_source=newsletter&amp;utm_medium=email&amp;utm_campaign=launch</code>{" "}
            to any link you share and it shows up here.
          </p>
          <table className="adm-table">
            <thead>
              <tr>
                <th scope="col">Source</th>
                <th scope="col">Medium</th>
                <th scope="col">Campaign</th>
                <th scope="col">Views</th>
              </tr>
            </thead>
            <tbody>
              {(data.campaigns || []).map((c, i) => (
                <tr key={i}>
                  <td>{c.source}</td>
                  <td>{c.medium}</td>
                  <td>{c.campaign}</td>
                  <td>{c.views.toLocaleString()}</td>
                </tr>
              ))}
              {!data.campaigns?.length && (
                <tr>
                  <td colSpan="4" className="adm-empty">
                    No tagged links used yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </div>
    </>
  );
};

export default TrafficSources;
